import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useChildren } from "@/context/ChildrenContext";
import { UserAvatar } from "@/components/UserAvatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Crown, Users, LogOut, Images, Gift, Share2, Trash2, ChevronDown } from "lucide-react";
import { toast } from "sonner";

export default function ProfileMenu() {
  const { user, logout } = useAuth();
  const { kids, removeChild } = useChildren();
  const [childrenOpen, setChildrenOpen] = useState(false);

  const deleteChild = async (c) => {
    await removeChild(c.id);
    toast.success(`Profil de ${c.name} supprimé`);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="profile-menu-btn"
            className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-[#E3E8F1] transition-colors duration-200"
          >
            <UserAvatar name={user?.name} picture={user?.picture} />
            <span className="hidden sm:block text-[#44506B] text-sm font-medium">{user?.name}</span>
            <ChevronDown className="h-4 w-4 text-[#99A5BB]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60 rounded-2xl p-1" data-testid="profile-menu-content">
          <DropdownMenuLabel className="px-3 py-2">
            <p className="font-semibold text-[#16233D]">{user?.name}</p>
            <p className="text-xs font-normal text-[#99A5BB] truncate">{user?.email}</p>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/abonnement" data-testid="menu-subscription" className="cursor-pointer rounded-xl">
              <Crown className="mr-2 h-4 w-4 text-[#E8899F]" /> Mon accès
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="menu-children"
            onClick={() => setChildrenOpen(true)}
            className="cursor-pointer rounded-xl"
          >
            <Users className="mr-2 h-4 w-4 text-[#23375E]" /> Mes enfants
            <span className="ml-auto text-xs text-[#99A5BB]">{kids.length}</span>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/album" data-testid="menu-album" className="cursor-pointer rounded-xl">
              <Images className="mr-2 h-4 w-4 text-[#23375E]" /> Album souvenirs
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/parrainage" data-testid="menu-referrals" className="cursor-pointer rounded-xl">
              <Share2 className="mr-2 h-4 w-4 text-[#23375E]" /> Parrainage
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/cadeaux" data-testid="menu-gifts" className="cursor-pointer rounded-xl">
              <Gift className="mr-2 h-4 w-4 text-[#23375E]" /> Cadeaux
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="cursor-pointer rounded-xl text-red-600 focus:text-red-600">
            <LogOut className="mr-2 h-4 w-4" /> Se déconnecter
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={childrenOpen} onOpenChange={setChildrenOpen}>
        <DialogContent className="rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="manage-children-dialog">
          <DialogHeader>
            <DialogTitle>Mes enfants</DialogTitle>
            <DialogDescription>Consultez et supprimez les profils de vos enfants.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {kids.length === 0 && (
              <p className="text-[#77849C] text-sm">
                Aucun profil pour l'instant. Ajoutez un enfant depuis le bouton « Enfant » en haut de l'écran.
              </p>
            )}
            {kids.map((c) => (
              <div
                key={c.id}
                data-testid={`manage-child-${c.id}`}
                className="flex items-center gap-3 rounded-2xl border border-[#E3E8F1] bg-white px-4 py-3"
              >
                <span className="text-2xl">{c.avatar_emoji || "🧒"}</span>
                <div className="flex-1">
                  <p className="font-semibold text-[#16233D]">{c.name}</p>
                  <p className="text-xs text-[#99A5BB]">{c.age} ans</p>
                </div>
                <button
                  data-testid={`delete-child-${c.id}`}
                  onClick={() => deleteChild(c)}
                  className="text-[#99A5BB] hover:text-red-500 transition-colors duration-200"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
